import { Navigation, Calendar, ChevronRight, Play, Square, MapPin } from 'lucide-react';
import type { Trip } from '../../types/trip';
import type { UserRole } from '../../types/user';
import { Button } from '../ui/Button';
import { TripStatusBadge } from './TripStatusBadge';
import {
  TRIP_DATE_FILTERS,
  formatDate,
  formatDateTime,
  formatDesviacion,
  formatTime,
  desviacionInicioMin,
  puedeFinalizar,
  puedeIniciar,
  type TripDateFilter,
} from '../../utils/trips';

interface TripHistoryProps {
  trips: Trip[];
  isLoading: boolean;
  error: Error | null;
  dateFilter: TripDateFilter;
  onDateFilterChange: (filtro: TripDateFilter) => void;
  onSelectTrip: (trip: Trip) => void;
  /** Rol y conductor de quien mira — deciden qué botón de acción se ve. */
  role?: UserRole;
  miDriverId?: string;
  onIniciar?: (trip: Trip) => void;
  onFinalizar?: (trip: Trip) => void;
  accionEnCurso?: string | null;
}

/**
 * FE-11 — histórico de recorridos, ya contra `GET /trips`.
 *
 * Dos cosas cambiaron respecto de la versión con mocks, y las dos son de
 * fondo:
 *  - El filtro de fechas **lo aplica el servidor por solape**, no este
 *    componente comparando una fecha suelta.
 *  - La tarjeta muestra la **ventana que manda** (`window`) y, cuando existe,
 *    la desviación frente a lo programado. No muestra distancia: el
 *    kilometraje no lo entrega el backend y sumar la polyline no es el
 *    odómetro.
 */
export function TripHistory({
  trips, isLoading, error, dateFilter, onDateFilterChange, onSelectTrip,
  role, miDriverId, onIniciar, onFinalizar, accionEnCurso,
}: TripHistoryProps) {
  return (
    <div className="trip-history-card" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Navigation size={18} style={{ color: 'var(--accent)' }} />
            Histórico de Recorridos
          </h3>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>
            {isLoading ? 'Cargando…' : `${trips.length} ${trips.length === 1 ? 'viaje' : 'viajes'}`}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
          {TRIP_DATE_FILTERS.map(f => (
            <button
              key={f.id}
              id={`trip-filter-${f.id}`}
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: 'var(--radius-md)',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                background: dateFilter === f.id ? 'var(--accent)' : 'var(--bg-raised)',
                color: dateFilter === f.id ? '#FFF' : 'var(--text-secondary)',
                cursor: 'pointer',
                transition: 'all var(--trans-fast)',
              }}
              onClick={() => onDateFilterChange(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '520px', overflowY: 'auto', paddingRight: '0.25rem' }}>
        {error ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: '#EF4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-lg)', fontSize: '13px' }}>
            No se pudieron cargar los viajes: {error.message}
          </div>
        ) : isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-raised)', borderRadius: 'var(--radius-lg)' }}>
            Cargando recorridos…
          </div>
        ) : trips.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-raised)', borderRadius: 'var(--radius-lg)' }}>
            No hay recorridos registrados en este periodo.
          </div>
        ) : (
          trips.map(trip => {
            const desvio = formatDesviacion(desviacionInicioMin(trip));
            const mostrarIniciar = !!onIniciar && puedeIniciar(role, trip, miDriverId);
            const mostrarFinalizar = !!onFinalizar && puedeFinalizar(role, trip, miDriverId);
            const ocupado = accionEnCurso === trip.id;

            return (
              <div
                key={trip.id}
                id={`trip-card-${trip.id}`}
                style={{
                  background: 'var(--bg-raised)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease, border-color 0.15s ease',
                }}
                onClick={() => onSelectTrip(trip)}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    <Calendar size={13} style={{ color: 'var(--accent)' }} />
                    {/* Un viaje sin ventana (cancelado) se fecha por lo programado:
                        es lo único cierto que queda de él. */}
                    <span>{formatDate(trip.window?.from ?? trip.scheduledFrom)}</span>
                    {trip.window && <span style={{ color: 'var(--text-muted)' }}>· {formatTime(trip.window.from)}</span>}
                  </div>
                  <TripStatusBadge status={trip.status} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3B82F6', display: 'inline-block', flexShrink: 0 }} />
                    {trip.origin}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', display: 'inline-block', flexShrink: 0 }} />
                    {trip.destination}
                  </div>
                </div>

                {trip.status === 'cancelled' && trip.cancelledReason && (
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Cancelado: “{trip.cancelledReason}”
                  </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '12px', color: 'var(--text-muted)', borderTop: '1px solid var(--border)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={12} /> {trip.vehiclePlate || 'Sin placa'}
                    </span>
                    {trip.window && (
                      <span>{formatTime(trip.window.from)} – {formatTime(trip.window.to)}</span>
                    )}
                    {desvio && desvio !== 'a tiempo' && (
                      <span style={{ color: (desviacionInicioMin(trip) ?? 0) > 0 ? '#F59E0B' : '#10B981', fontWeight: 700 }}>
                        Salió {desvio}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
                    {mostrarIniciar && (
                      <Button size="sm" variant="primary" isLoading={ocupado} leftIcon={<Play size={13} />} onClick={() => onIniciar!(trip)}>
                        Iniciar
                      </Button>
                    )}
                    {mostrarFinalizar && (
                      <Button size="sm" variant="success" isLoading={ocupado} leftIcon={<Square size={13} />} onClick={() => onFinalizar!(trip)}>
                        Finalizar
                      </Button>
                    )}
                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px', color: 'var(--accent)', fontWeight: 600 }}>
                      Detalle <ChevronRight size={14} />
                    </span>
                  </div>
                </div>

                {trip.status === 'planned' && !trip.window && (
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Programado para {formatDateTime(trip.scheduledFrom)}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
